/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Eye, EyeOff, FolderOpen, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SshHostCombobox, SshConfigAlias } from './SshHostCombobox';
import { defaultAuthForKind } from './sshAuthDefaults';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { ruleValidators } from '../connection-dialog/state/fieldRuleValidators';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { CHECK_ROW_CLASS, CHECKBOX_CLASS, INVALID_CLASS, SELECT_CLASS } from '../connection-dialog/fields/fieldControls';
import { Input } from '@/components/ui/input';

type FormApi = any;

interface Props {
  form: FormApi;
}

/**
 * M10 — inline feedback as the user types: each SSH field's `onChange` / `onBlur` validators come from the shared
 * connection field rules (Option C C0, `connectionFieldRules.ts`), which answer nothing once SSH is switched off — the
 * field instances outlive this section, and a stale "SSH user is required" used to block Save invisibly (B8).
 */
const SSH_CAUSES = ['onChange', 'onBlur'] as const;

/** Read the first error message off a TanStack-Form field state. */
function firstError(meta: { errors?: unknown[] } | undefined): string | undefined {
  const errs = meta?.errors;
  if (!errs || errs.length === 0) return undefined;
  const first = errs[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'message' in first) {
    const m = (first as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return undefined;
}

/** Renders an inline error below a field if one is present. */
function FieldError({ testId, message }: { testId: string; message?: string }) {
  if (!message) return null;
  return (
    <p data-testid={testId} className="mt-1 text-xs text-destructive">
      {message}
    </p>
  );
}

/** Tracks which SSH fields were pre-filled from a ssh-config alias selection. */
type ChippedFields = {
  host?: boolean;
  port?: boolean;
  user?: boolean;
  auth?: boolean;
};

export function SshTunnelFields({ form }: Props) {
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [chipped, setChipped] = useState<ChippedFields>({});
  const { track } = useAnalytics();

  const handleSelectAlias = (alias: SshConfigAlias) => {
    const nextChipped: ChippedFields = {};

    const host = alias.hostName ?? alias.alias;
    form.setFieldValue('sshTunnel.host', host);
    nextChipped.host = true;

    form.setFieldValue('sshTunnel.sshConfigAlias', alias.alias);

    if (alias.port !== undefined) {
      form.setFieldValue('sshTunnel.port', alias.port);
      nextChipped.port = true;
    }
    if (alias.user !== undefined) {
      form.setFieldValue('sshTunnel.user', alias.user);
      nextChipped.user = true;
    }
    if (alias.identityFile) {
      form.setFieldValue('sshTunnel.auth', {
        kind: 'key',
        keyPath: alias.identityFile,
        hasStoredPassphrase: false,
      });
      nextChipped.auth = true;
    }

    setChipped(nextChipped);
  };

  const clearChip = (field: keyof ChippedFields) => {
    setChipped((prev) => ({ ...prev, [field]: false }));
  };

  return (
    <FormRow row="sshTunnel" label="SSH tunnel" groupId="ssh-tunnel-label">
      {/* Always-visible checkbox; the SSH fields open under it, in the control column (Security section, C2). */}
      <form.Field name="sshTunnel.enabled">
        {(field: any) => (
          <div className={CHECK_ROW_CLASS}>
            <input
              id="ssh-tunnel-enabled"
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={!!field.state.value}
              onChange={(e) => {
                const next = e.target.checked;
                field.handleChange(next);
                track('toggle:ssh_tunnel', { enabled: next });
              }}
              onBlur={field.handleBlur}
              data-testid="ssh-tunnel-enabled-checkbox"
            />
            <label htmlFor="ssh-tunnel-enabled">Connect through an SSH tunnel</label>
          </div>
        )}
      </form.Field>

      {/* Expanded fields — visible only when enabled */}
      <form.Subscribe selector={(state: any) => !!state.values.sshTunnel?.enabled}>
        {(enabled: boolean) =>
          enabled ? (
            <div className="mt-2 space-y-3 rounded-md border border-border bg-muted/20 p-3" data-testid="ssh-tunnel-fields">
              {/* SSH Host */}
              <form.Field
                name="sshTunnel.host"
                validators={ruleValidators('sshTunnel.host', SSH_CAUSES)}
              >
                {(field: any) => (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label htmlFor="ssh-host" className="block text-sm font-medium">SSH host</label>
                      {chipped.host && (
                        <Chip onClear={() => clearChip('host')} />
                      )}
                    </div>
                    <SshHostCombobox
                      id="ssh-host"
                      invalid={!!firstError(field.state.meta)}
                      value={field.state.value ?? ''}
                      onChange={(v) => {
                        field.handleChange(v);
                        clearChip('host');
                      }}
                      onSelectAlias={handleSelectAlias}
                    />
                    <FieldError testId="ssh-host-error" message={firstError(field.state.meta)} />
                    {/* Forget saved host key — small inline link. Only shown
                        when the host field has a value to forget. Used after
                        a legitimate bastion key rotation so the next connect
                        re-TOFUs the new key instead of refusing with
                        SSH_HOST_KEY_CHANGED. */}
                    <form.Subscribe selector={(state: any) => ({
                      host: state.values.sshTunnel?.host ?? '',
                      port: state.values.sshTunnel?.port ?? 22,
                    })}>
                      {({ host, port }: { host: string; port: number }) =>
                        host.length > 0 ? (
                          <button
                            type="button"
                            className="mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline hover:text-foreground"
                            data-testid="ssh-forget-host-button"
                            onClick={async () => {
                              try {
                                await window.electronAPI?.ssh?.forgetHost?.(host, port);
                                toast.success({
                                  title: 'Saved SSH host key cleared',
                                  description: `Next connection to ${host} will re-trust the bastion.`,
                                  duration: 5000,
                                });
                                track('feature:ssh_forget_host');
                              } catch {
                                toast.error({
                                  title: "Couldn't clear saved host key",
                                  description: 'Check ~/.ssh/jam_known_hosts permissions and try again.',
                                  duration: 7000,
                                });
                              }
                            }}
                          >
                            Forget saved host key
                          </button>
                        ) : null
                      }
                    </form.Subscribe>
                  </div>
                )}
              </form.Field>

              {/* Port + User row */}
              <div className="flex gap-3">
                <form.Field
                  name="sshTunnel.port"
                  validators={ruleValidators('sshTunnel.port', SSH_CAUSES)}
                >
                  {(field: any) => (
                    <div className="w-24">
                      <div className="flex items-center gap-1 mb-1">
                        <label htmlFor="ssh-port" className="block text-sm font-medium">SSH port</label>
                        {chipped.port && (
                          <Chip onClear={() => clearChip('port')} />
                        )}
                      </div>
                      <Input
                        id="ssh-port"
                        type="number"
                        min={1}
                        max={65535}
                        className={firstError(field.state.meta) ? INVALID_CLASS : undefined}
                        value={field.state.value ?? 22}
                        onChange={(e) => {
                          field.handleChange(Number(e.target.value));
                          clearChip('port');
                        }}
                        onBlur={field.handleBlur}
                        data-testid="ssh-port-input"
                      />
                      <FieldError testId="ssh-port-error" message={firstError(field.state.meta)} />
                    </div>
                  )}
                </form.Field>

                <form.Field
                  name="sshTunnel.user"
                  validators={ruleValidators('sshTunnel.user', SSH_CAUSES)}
                >
                  {(field: any) => (
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-1">
                        <label htmlFor="ssh-user" className="block text-sm font-medium">SSH user</label>
                        {chipped.user && (
                          <Chip onClear={() => clearChip('user')} />
                        )}
                      </div>
                      <Input
                        id="ssh-user"
                        className={firstError(field.state.meta) ? INVALID_CLASS : undefined}
                        value={field.state.value ?? ''}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          clearChip('user');
                        }}
                        onBlur={field.handleBlur}
                        data-testid="ssh-user-input"
                      />
                      <FieldError testId="ssh-user-error" message={firstError(field.state.meta)} />
                    </div>
                  )}
                </form.Field>
              </div>

              {/* Auth kind selector */}
              <form.Field name="sshTunnel.auth">
                {(field: any) => (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label htmlFor="ssh-auth" className="block text-sm font-medium">SSH authentication</label>
                      {chipped.auth && (
                        <Chip onClear={() => clearChip('auth')} />
                      )}
                    </div>
                    <select
                      id="ssh-auth"
                      className={SELECT_CLASS}
                      value={field.state.value?.kind ?? 'agent'}
                      onChange={(e) => {
                        const kind = e.target.value as 'agent' | 'key' | 'password';
                        const prev = field.state.value;
                        // No-op when the user reselects the same kind — don't
                        // clobber keyPath / hasStoredPassphrase or clear the
                        // ssh-config chip on a non-change.
                        if (prev?.kind === kind) return;
                        clearChip('auth');
                        const next = defaultAuthForKind(kind);
                        // Preserve any prior keyPath the user already configured
                        // when transitioning INTO the key kind from another kind
                        // (the field's defaults are blank).
                        if (next.kind === 'key' && typeof prev?.keyPath === 'string' && prev.keyPath.length > 0) {
                          field.handleChange({ ...next, keyPath: prev.keyPath });
                        } else {
                          field.handleChange(next);
                        }
                        track('feature:ssh_auth_kind_changed', { kind });
                      }}
                      onBlur={field.handleBlur}
                      data-testid="ssh-auth-select"
                    >
                      <option value="agent">SSH agent</option>
                      <option value="key">Key file</option>
                      <option value="password">Password</option>
                    </select>
                  </div>
                )}
              </form.Field>

              {/* Auth-kind-specific fields */}
              <form.Subscribe selector={(state: any) => state.values.sshTunnel?.auth?.kind ?? 'agent'}>
                {(authKind: string) => (
                  <>
                    {authKind === 'key' && <KeyFields form={form} showPassphrase={showPassphrase} onTogglePassphrase={() => setShowPassphrase((v) => !v)} track={track} />}
                    {authKind === 'password' && <PasswordField form={form} showPassword={showPassword} onTogglePassword={() => setShowPassword((v) => !v)} track={track} />}
                  </>
                )}
              </form.Subscribe>
            </div>
          ) : null
        }
      </form.Subscribe>
    </FormRow>
  );
}

/** Small "from ssh-config" chip with a clear button. */
function Chip({ onClear }: { onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
      from ssh-config
      <button
        type="button"
        onClick={onClear}
        className="ml-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
        aria-label="Clear ssh-config value"
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

interface KeyFieldsProps {
  form: FormApi;
  showPassphrase: boolean;
  onTogglePassphrase: () => void;
  track: TrackFunction;
}

function KeyFields({ form, showPassphrase, onTogglePassphrase, track }: KeyFieldsProps) {
  return (
    <>
      <form.Field
        name="sshTunnel.auth"
        // The key file lives inside the `auth` union: the rule validates it only while the kind is "key".
        validators={ruleValidators('sshTunnel.auth', SSH_CAUSES)}
      >
        {(field: any) => (
          <div>
            <label htmlFor="ssh-keypath" className="block text-sm font-medium mb-1">Key file</label>
            <div className="flex gap-2">
              <Input
                id="ssh-keypath"
                className={firstError(field.state.meta) ? INVALID_CLASS : undefined}
                value={field.state.value?.keyPath ?? ''}
                onChange={(e) =>
                  field.handleChange({ ...field.state.value, keyPath: e.target.value })
                }
                onBlur={field.handleBlur}
                placeholder="~/.ssh/id_rsa"
                data-testid="ssh-keypath-input"
              />
              <Button
                type="button"
                variant="outline"
                aria-label="Browse for the key file"
                data-testid="ssh-keypath-browse"
                onClick={async () => {
                  try {
                    const picked = await window.electronAPI?.ssh?.openKeyFile?.();
                    if (picked) {
                      field.handleChange({ ...field.state.value, keyPath: picked });
                      track('feature:ssh_key_path_picked', { picked: true });
                    }
                  } catch {
                    // ignore
                  }
                }}
              >
                <FolderOpen className="h-4 w-4" />
              </Button>
            </div>
            <FieldError testId="ssh-keypath-error" message={firstError(field.state.meta)} />
          </div>
        )}
      </form.Field>

      <form.Field name="sshTunnel.passphrase">
        {(field: any) => (
          <div>
            <label htmlFor="ssh-passphrase" className="block text-sm font-medium mb-1">
              Passphrase <span className="text-xs font-normal text-muted-foreground">optional</span>
            </label>
            <div className="relative">
              <Input
                id="ssh-passphrase"
                type={showPassphrase ? 'text' : 'password'}
                className="pr-10"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                data-testid="ssh-passphrase-input"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                onClick={onTogglePassphrase}
                tabIndex={-1}
                aria-label={showPassphrase ? 'Hide passphrase' : 'Show passphrase'}
              >
                {showPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* M13 — when the user is editing a connection that already has
                a keychain-saved passphrase, the input is intentionally blank
                (we never round-trip the secret to the renderer). Show an
                inline hint so they understand "blank doesn't mean lost" and
                that typing replaces the saved value. */}
            <form.Subscribe selector={(state: any) => ({
              hasStored: !!state.values.sshTunnel?.auth?.hasStoredPassphrase,
              hasInput: (state.values.sshTunnel?.passphrase ?? '').length > 0,
            })}>
              {({ hasStored, hasInput }: { hasStored: boolean; hasInput: boolean }) =>
                hasStored && !hasInput ? (
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid="ssh-passphrase-saved-hint"
                  >
                    Saved · type to replace
                  </p>
                ) : null
              }
            </form.Subscribe>
          </div>
        )}
      </form.Field>

      <form.Field name="sshTunnel.auth.hasStoredPassphrase">
        {(field: any) => (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={!!field.state.value}
              onChange={(e) => {
                const next = e.target.checked;
                field.handleChange(next);
                track('toggle:ssh_save_passphrase', { enabled: next });
              }}
              data-testid="ssh-keypassphrase-save-checkbox"
            />
            Save passphrase in keychain
          </label>
        )}
      </form.Field>
    </>
  );
}

interface PasswordFieldProps {
  form: FormApi;
  showPassword: boolean;
  onTogglePassword: () => void;
  track: TrackFunction;
}

function PasswordField({ form, showPassword, onTogglePassword, track }: PasswordFieldProps) {
  return (
    <>
      <form.Field name="sshTunnel.password">
        {(field: any) => (
          <div>
            <label htmlFor="ssh-password" className="block text-sm font-medium mb-1">SSH password</label>
            <div className="relative">
              <Input
                id="ssh-password"
                type={showPassword ? 'text' : 'password'}
                className="pr-10"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                data-testid="ssh-password-input"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                onClick={onTogglePassword}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide SSH password' : 'Show SSH password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* M13 — see KeyFields.passphrase for the rationale. */}
            <form.Subscribe selector={(state: any) => ({
              hasStored: !!state.values.sshTunnel?.auth?.hasStoredPassword,
              hasInput: (state.values.sshTunnel?.password ?? '').length > 0,
            })}>
              {({ hasStored, hasInput }: { hasStored: boolean; hasInput: boolean }) =>
                hasStored && !hasInput ? (
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid="ssh-password-saved-hint"
                  >
                    Saved · type to replace
                  </p>
                ) : null
              }
            </form.Subscribe>
          </div>
        )}
      </form.Field>

      <form.Field name="sshTunnel.auth.hasStoredPassword">
        {(field: any) => (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={!!field.state.value}
              onChange={(e) => {
                const next = e.target.checked;
                field.handleChange(next);
                track('toggle:ssh_save_password', { enabled: next });
              }}
              data-testid="ssh-password-save-checkbox"
            />
            Save password in keychain
          </label>
        )}
      </form.Field>
    </>
  );
}
